import React, { useState, useEffect } from 'react';
import { 
  X, 
  Smartphone, 
  LogIn, 
  Navigation, 
  MapPin, 
  Camera, 
  Package, 
  ShoppingBag, 
  LogOut, 
  XCircle, 
  Coffee, 
  Activity,
  CheckCircle2,
  Trash2,
  Phone,
  MessageCircle,
  FileText,
  TrendingUp,
  MapPinned,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart as RechartsPieChart, 
  Pie, 
  Cell
} from 'recharts';
import { FSR_DATA, TIMELINE_EVENTS } from '../data/operationsData';

interface FSRDetailPanelProps {
  fsrId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
}

export const FSRDetailPanel: React.FC<FSRDetailPanelProps> = ({ fsrId, isOpen, onClose, onNext, onPrev }) => {
  const [activeTab, setActiveTab] = useState<'timeline' | 'map' | 'bookings' | 'photos' | 'performance'>('timeline');
  const [animatedStats, setAnimatedStats] = useState({ done: 0, bookings: 0, value: 0, photos: 0 });

  const fsr = FSR_DATA.find(f => f.id === fsrId) || FSR_DATA[0];

  useEffect(() => {
    if (isOpen) {
      setActiveTab('timeline');
      // KPI Animation
      const duration = 800;
      const startTime = performance.now();
      const initialStats = { done: fsr.todayDone, bookings: fsr.todayBookings, value: fsr.todayValue, photos: fsr.todayPhotos };

      const animate = (time: number) => {
        const progress = Math.min((time - startTime) / duration, 1);
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        
        setAnimatedStats({
          done: Math.floor(easeOutQuart * initialStats.done),
          bookings: Math.floor(easeOutQuart * initialStats.bookings),
          value: Math.floor(easeOutQuart * initialStats.value),
          photos: Math.floor(easeOutQuart * initialStats.photos)
        });

        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }
  }, [isOpen, fsrId]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { e.key === 'Escape' && onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const renderIcon = (Icon: any, color: string) => (
    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: color }}>
      <Icon size={15} />
    </div>
  );

  const StoreSimulation = ({ name, time, verified = true }: { name: string, time: string, verified?: boolean }) => (
    <div className="mt-2 rounded-xl overflow-hidden border border-[#F1F5F9] shadow-sm">
      <div className="h-[100px] relative bg-[#1C2B3A]">
        {/* Sky */}
        <div className="absolute top-0 left-0 right-0 h-[38%] bg-gradient-to-b from-[#87CEEB] to-[#4A90D9]" />
        {/* Sun */}
        <div className="absolute top-1.5 right-3.5 w-3.5 h-3.5 bg-[#FEF08A] rounded-full shadow-[0_0_14px_#FEF08A]" />
        {/* Ground */}
        <div className="absolute bottom-0 left-0 right-0 h-[12%] bg-[#374151]" />
        {/* Building */}
        <div className="absolute bottom-[12%] left-1/2 -translate-x-1/2 w-[65%] h-[52%] bg-[#F8FAFC] border-[1.5px] border-[#E2E8F0]">
          {/* Sign */}
          <div className="absolute top-1 left-1/2 -translate-x-1/2 w-[80%] h-4 bg-[#4F46E5] rounded-[3px] flex items-center justify-center">
            <span className="text-white text-[8px] font-bold truncate px-1 uppercase">{name}</span>
          </div>
          {/* Windows */}
          <div className="absolute top-6 left-0 right-0 px-2 grid grid-cols-2 gap-1">
            <div className="h-2.5 bg-[#FEF3C7] border border-[#F59E0B] rounded-[2px]" />
            <div className="h-2.5 bg-[#FEF3C7] border border-[#F59E0B] rounded-[2px]" />
            <div className="h-2.5 bg-[#FEF3C7] border border-[#F59E0B] rounded-[2px]" />
            <div className="h-2.5 bg-[#FEF3C7] border border-[#F59E0B] rounded-[2px]" />
          </div>
          {/* Door */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3.5 h-5.5 bg-[#92400E] rounded-t-[2px]" />
        </div>
        {/* Overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-[45%] bg-gradient-to-t from-black/75 to-transparent flex items-end p-2">
          <span className="text-white text-[9px] font-semibold italic">{time} · GPS Verified</span>
        </div>
        {verified && (
          <div className="absolute top-1.5 right-1.5 bg-[#10B981]/90 text-white text-[8px] font-bold px-1.5 h-5 flex items-center rounded-md uppercase italic tracking-widest">
            ✓ Verified
          </div>
        )}
      </div>
      <div className="flex gap-1.5 p-2 bg-[#F0FDF4] border-t border-[#A7F3D0]">
        <span className="bg-white/70 text-[#059669] text-[9px] font-bold px-1.5 py-0.5 rounded border border-[#A7F3D0] uppercase italic">✓ GPS Match</span>
        <span className="bg-white/70 text-[#059669] text-[9px] font-bold px-1.5 py-0.5 rounded border border-[#A7F3D0] uppercase italic">✓ Store Front</span>
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/35 backdrop-blur-[2px] z-40"
          />
          <motion.div 
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-screen w-[560px] bg-white z-50 shadow-[-8px_0_32px_rgba(0,0,0,0.2)] flex flex-col"
          >
            {/* PANEL HEADER */}
            <header className="shrink-0 border-b border-[#F1F5F9] p-5">
              <div className="flex justify-between items-start">
                <div className="flex gap-4 items-center">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg italic" style={{ backgroundColor: fsr.color }}>
                      {fsr.avatar}
                    </div>
                    <div className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-[2.5px] border-white bg-emerald-500 shadow-md" />
                  </div>
                  <div>
                    <h2 className="text-[20px] font-black text-[#0F172A] uppercase italic leading-none">{fsr.name}</h2>
                    <div className="text-[12px] text-[#94A3B8] font-bold mt-1.5 uppercase tracking-widest leading-none">
                      {fsr.emp} · {fsr.distributor}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                   <button className="h-8 px-4 bg-white border border-[#E2E8F0] shadow-sm rounded-lg text-[11px] font-black text-[#4F46E5] uppercase italic hover:bg-slate-50">WhatsApp ↗</button>
                   <button onClick={onClose} className="w-8 h-8 rounded-full bg-[#F1F5F9] flex items-center justify-center text-[#64748B] hover:bg-[#E2E8F0] hover:text-[#0F172A]"><X size={18} /></button>
                </div>
              </div>

              <div className="mt-4 flex gap-2 flex-wrap">
                <div className="px-2.5 py-1 bg-[#EFF6FF] text-[#3B82F6] rounded-full text-[10px] font-bold uppercase tracking-wide border border-[#DBEAFE] italic leading-none flex items-center gap-1.5">
                   <div className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" /> {fsr.currentStatus}
                </div>
                <div className="px-2.5 py-1 bg-[#EEF2FF] text-[#4F46E5] rounded-full text-[10px] font-bold uppercase tracking-wide italic leading-none">
                   {fsr.checkIn ? `In since ${fsr.checkIn}` : 'Not checked in'}
                </div>
                <div className="px-2.5 py-1 bg-[#F1F5F9] text-[#64748B] rounded-full text-[10px] font-bold uppercase tracking-wide italic leading-none">
                   {fsr.kmCovered}KM TODAY
                </div>
                <div className="px-2.5 py-1 bg-[#FFFBEB] text-[#D97706] rounded-full text-[10px] font-bold uppercase tracking-wide italic leading-none">
                   🔥 {fsr.streak}D STREAK
                </div>
                <div className="px-2.5 py-1 bg-[#F5F3FF] text-[#7C3AED] rounded-full text-[10px] font-bold uppercase tracking-wide italic leading-none">
                   RANK #{fsr.rank} TODAY
                </div>
              </div>
            </header>

            {/* INNER TABS */}
            <div className="flex border-b border-[#F1F5F9] px-5 bg-[#FAFBFF]">
               {(['timeline', 'map', 'bookings', 'photos', 'performance'] as const).map(tab => (
                 <button 
                  key={tab} onClick={() => setActiveTab(tab)}
                  className={`h-11 px-3 text-[13px] font-black uppercase tracking-widest transition-all relative
                    ${activeTab === tab ? 'text-[#4F46E5]' : 'text-[#94A3B8] hover:text-[#64748B]'}`}
                 >
                   {tab}
                   {activeTab === tab && <motion.div layoutId="panel-tab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#4F46E5] mx-2" />}
                 </button>
               ))}
            </div>

            {/* BODY */}
            <div className="flex-1 overflow-y-auto p-5 custom-scrollbar bg-white">
               {activeTab === 'timeline' && (
                 <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                    <div className="bg-[#F8FAFC] rounded-xl p-3 grid grid-cols-4 gap-2 border border-[#F1F5F9] shadow-sm mb-4">
                       <div className="text-center">
                          <div className="text-[20px] font-black bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent italic leading-none">{animatedStats.done}</div>
                          <div className="text-[9px] font-black text-[#94A3B8] uppercase tracking-widest mt-1.5 leading-none">Stores</div>
                       </div>
                       <div className="text-center">
                          <div className="text-[20px] font-black bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent italic leading-none">{animatedStats.bookings}</div>
                          <div className="text-[9px] font-black text-[#94A3B8] uppercase tracking-widest mt-1.5 leading-none">Bookings</div>
                       </div>
                       <div className="text-center">
                          <div className="text-[20px] font-black bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent italic leading-none">₹{(animatedStats.value/1000).toFixed(0)}K</div>
                          <div className="text-[9px] font-black text-[#94A3B8] uppercase tracking-widest mt-1.5 leading-none">Value</div>
                       </div>
                       <div className="text-center">
                          <div className="text-[20px] font-black bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent italic leading-none">{animatedStats.photos}</div>
                          <div className="text-[9px] font-black text-[#94A3B8] uppercase tracking-widest mt-1.5 leading-none">Photos</div>
                       </div>
                    </div>

                    <div className="relative pl-7 ml-1">
                       <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#4F46E5] to-[#E2E8F0] shadow-sm" />
                       {TIMELINE_EVENTS.map((event, i) => (
                         <div key={event.id} className="mb-8 relative last:mb-0">
                            <div className="absolute -left-[35px] top-0">
                               {renderIcon(event.icon, event.color)}
                               {event.isLive && (
                                 <div className="absolute inset-0 w-9 h-9 border-[3px] border-emerald-500 rounded-full animate-ping opacity-30" />
                               )}
                            </div>
                            <div className="flex justify-between items-start mb-1">
                               <h4 className="text-[14px] font-black text-[#0F172A] uppercase italic leading-none">{event.label}</h4>
                               <span className="text-[11px] font-bold text-[#94A3B8] leading-none">{event.time}</span>
                            </div>
                            <p className="text-[13px] text-[#64748B] leading-tight italic font-medium">{event.detail}</p>
                            
                            {event.duration && (
                              <div className="mt-2 h-5 flex items-center gap-1.5 bg-[#EEF2FF] text-[#4F46E5] px-2 rounded-full text-[10px] font-black uppercase italic tracking-widest w-fit">
                                 ⏱ {event.duration}
                              </div>
                            )}

                            {event.type === 'photo' && <StoreSimulation name={event.storeName || 'Store'} time={event.time} />}
                            
                            {event.type === 'order' && (
                              <div className="mt-3 bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl p-3 flex justify-between items-center shadow-sm">
                                <div>
                                   <div className="text-[11px] font-mono font-bold text-[#059669] leading-none mb-1">BK-0407-00X</div>
                                   <div className="flex gap-1 mt-1.5">
                                      {['Shirt', 'Kurta', 'Denim'].map((p, idx) => (
                                        <span key={idx} className="bg-white/60 text-[#059669] text-[9px] font-black px-1.5 py-0.5 rounded border border-[#A7F3D0] uppercase italic">{p}</span>
                                      ))}
                                   </div>
                                </div>
                                <div className="text-right">
                                   <div className="text-[17px] font-black text-[#059669] italic tracking-tighter leading-none">₹{event.amount?.toLocaleString()}</div>
                                   <div className="text-[9px] font-black text-[#10B981] uppercase tracking-widest mt-1">SUCCESSFUL ✓</div>
                                </div>
                              </div>
                            )}

                            {event.type === 'noorder' && (
                               <div className="mt-2 bg-[#FFFBEB] border border-[#FDE68A] rounded-xl p-3 flex items-center gap-2.5 shadow-sm">
                                  <XCircle size={15} className="text-[#D97706] shrink-0" />
                                  <div className="text-[12px] font-bold text-[#92400E] leading-tight italic">{event.detail}</div>
                               </div>
                            )}

                            {event.type === 'travel' && (
                              <div className="mt-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-2 px-3 flex items-center justify-between shadow-sm">
                                 <div className="flex items-center gap-2 overflow-hidden">
                                    <Navigation size={13} className="text-[#94A3B8] shrink-0" />
                                    <span className="text-[11px] font-bold text-[#64748B] truncate italic">{event.detail}</span>
                                 </div>
                                 <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest leading-none bg-white px-2 py-0.5 rounded shadow-sm border border-[#EEF2FF] italic">{event.duration}</span>
                              </div>
                            )}

                            {event.isLive && (
                               <div className="mt-2 bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl p-3 flex items-center gap-2.5 border-l-4 border-l-[#10B981] shadow-sm">
                                  <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                                  <div>
                                     <div className="text-[11px] font-black text-[#059669] uppercase italic tracking-widest leading-none">LIVE NOW</div>
                                     <div className="text-[11px] font-bold text-[#064E3B] mt-1 italic">{event.detail}</div>
                                  </div>
                               </div>
                            )}
                         </div>
                       ))}
                    </div>
                 </motion.div>
               )}

               {activeTab === 'map' && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col gap-5">
                     <div className="h-[240px] bg-[#1E293B] rounded-2xl relative overflow-hidden shadow-inner border-[3px] border-white ring-1 ring-[#F1F5F9]">
                        {/* CSS MINI MAP SIMULATION */}
                        <div className="absolute inset-0 opacity-10">
                           {[...Array(6)].map((_, i) => <div key={i} className="absolute w-full h-px bg-white" style={{ top: `${i*20}%` }} />)}
                           {[...Array(6)].map((_, i) => <div key={i} className="absolute h-full w-px bg-white" style={{ left: `${i*20}%` }} />)}
                        </div>
                        {/* ROUTE LINE */}
                        <svg className="absolute inset-0 opacity-40 w-full h-full pointer-events-none">
                           <path d="M 50 180 L 150 120 L 300 160 L 450 100" stroke={fsr.color} strokeWidth="3" fill="none" strokeDasharray="6 4" filter="blur(0.5px)" className="animate-pulse" />
                        </svg>
                        {/* MARKER */}
                        <div className="absolute top-[40%] left-[80%] -translate-x-1/2 -translate-y-1/2 z-10 transition-all">
                           <div className="relative">
                              <div className="absolute inset-0 -m-2 w-10 h-10 border-2 rounded-full border-indigo-400 animate-ping opacity-40" />
                              <div className="w-6 h-6 rounded-full border-2 border-white shadow-xl flex items-center justify-center text-white text-[8px] font-black italic" style={{ backgroundColor: fsr.color }}>{fsr.avatar}</div>
                           </div>
                        </div>
                        <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-md px-3 py-1.5 rounded-lg border border-white/10 text-[9px] font-black text-white/80 uppercase tracking-widest shadow-xl">
                           LIVE VIEW · AREA COVERED: {fsr.kmCovered}KM
                        </div>
                     </div>

                     <div className="space-y-4">
                        <span className="text-[11px] font-black text-indigo-600 italic uppercase tracking-[0.2em] block leading-none">TODAY'S ROUTE SEQUENCE</span>
                        {[
                          { step: 1, name: 'Style Zone', area: 'Andheri West', time: '9:12 AM', val: '₹50,640', icon: 'SZ', color: '#4F46E5', isDone: true },
                          { step: 2, name: 'Westside Express', area: 'Andheri West', time: '9:52 AM', val: '₹43,776', icon: 'WE', color: '#7C3AED', isDone: true },
                          { step: 3, name: 'Fashion Hub', area: 'Juhu Market', time: '10:26 AM', val: 'No Order', icon: 'FH', color: '#F59E0B', isDone: true },
                          { step: 4, name: 'Trendy Threads', area: 'Bandra West', time: '11:18 AM', val: '₹1,03,740', icon: 'TT', color: '#0891B2', isDone: true },
                          { step: 5, name: 'Kiran Fashion', area: 'Goregaon West', time: 'LIVE', val: '● Now', icon: 'KF', color: '#BE185D', isCurrent: true }
                        ].map((s, i) => (
                           <div key={i} className="flex items-center gap-4 bg-white border border-[#F1F5F9] rounded-xl p-4 shadow-sm hover:shadow-md transition-all group">
                              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center text-[11px] font-black shadow-md shrink-0 italic">{s.step}</div>
                              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-[12px] shrink-0 italic border-[2px] border-white shadow-sm" style={{ backgroundColor: s.color }}>{s.icon}</div>
                              <div className="flex-1 min-w-0">
                                 <h4 className="text-[14px] font-black text-[#0F172A] leading-tight uppercase italic truncate">{s.name}</h4>
                                 <div className="text-[11px] font-bold text-[#94A3B8] mt-0.5 uppercase tracking-widest">{s.area}</div>
                              </div>
                              <div className="text-right shrink-0">
                                 {s.isCurrent ? (
                                   <div className="text-[12px] font-black text-[#4F46E5] italic uppercase tracking-tighter animate-pulse flex items-center gap-1.5 justify-end">
                                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" /> ● LIVE
                                   </div>
                                 ) : (
                                   <>
                                     <div className={`text-[13px] font-black italic tracking-tighter ${s.val === 'No Order' ? 'text-amber-500' : 'text-[#059669]'}`}>{s.val}</div>
                                     <div className="text-[10px] font-bold text-[#94A3B8] mt-0.5 italic">{s.time}</div>
                                   </>
                                 )}
                              </div>
                           </div>
                        ))}
                     </div>
                  </motion.div>
               )}

               {activeTab === 'bookings' && (
                 <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
                    <div className="grid grid-cols-3 gap-3 mb-2">
                       <div className="bg-white border-2 border-[#F1F5F9] rounded-2xl p-4 text-center shadow-sm">
                          <div className="text-3xl font-black italic bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent leading-none">{fsr.todayBookings}</div>
                          <div className="text-[10px] font-black text-[#94A3B8] uppercase tracking-widest mt-2">Today</div>
                       </div>
                       <div className="col-span-2 bg-[#F0FDF4] border-2 border-[#D1FAE5] rounded-2xl p-4 flex items-center justify-center shadow-sm">
                          <div className="text-3xl font-black italic text-emerald-600 leading-none">₹{(fsr.todayValue/1000).toFixed(1)}K</div>
                          <div className="text-[10px] font-black text-emerald-500 uppercase tracking-widest ml-3">Value Captured</div>
                       </div>
                    </div>

                    <div className="space-y-3">
                       {[
                         { id: 'BK-0407-001', store: 'Style Zone', icon: 'SZ', amount: 50640, status: 'Approved', products: [{n: 'Oxford Shirt', q: 24}, {n: 'Floral Kurta', q: 18}], time: '9:19 AM' },
                         { id: 'BK-0407-004', store: 'Westside Express', icon: 'WE', amount: 43776, status: 'Approved', products: [{n: 'Summer Dress', q: 12}, {n: 'Oxford Shirt', q: 18}], time: '9:52 AM' },
                         { id: 'BK-0407-003', store: 'Trendy Threads', icon: 'TT', amount: 103740, status: 'Pending', products: [{n: 'Blue Denim', q: 36}, {n: 'Black Blazer', q: 8}], time: '11:18 AM' }
                       ].map((b, i) => (
                         <div key={i} className="bg-white border-2 border-[#F8FAFC] rounded-2xl p-4 shadow-sm relative overflow-hidden group hover:border-indigo-100 transition-all">
                            <div className={`absolute top-0 right-0 px-4 py-1 text-[10px] font-black uppercase tracking-widest italic shadow-lg
                              ${b.status === 'Approved' ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'}`}>
                               {b.status}
                            </div>
                            <div className="flex gap-4 items-center mb-4">
                               <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-[#94A3B8] font-black italic border border-slate-100">{b.icon}</div>
                               <div>
                                  <h4 className="text-[15px] font-black text-[#0F172A] uppercase italic leading-none">{b.store}</h4>
                                  <div className="text-[11px] font-bold text-[#94A3B8] mt-1.5 uppercase tracking-[0.15em] leading-none">{b.id} · {b.time}</div>
                               </div>
                            </div>
                            <div className="flex flex-wrap gap-2 mb-4">
                               {b.products.map((p, idx) => (
                                 <span key={idx} className="bg-[#F5F3FF] text-[#7C3AED] px-2.5 py-1 rounded-lg text-[10px] font-black uppercase italic border border-purple-100">
                                   {p.n} ×{p.q}
                                 </span>
                               ))}
                            </div>
                            <div className="flex justify-between items-end pt-3 border-t border-slate-50">
                               <div className="text-[12px] font-black text-emerald-500 uppercase italic flex items-center gap-1.5 tracking-tight border-b-2 border-emerald-100">
                                  <Camera size={13} /> Photo Proof Locked
                               </div>
                               <div className="text-[22px] font-black text-[#0F172A] italic tracking-tighter leading-none">₹{b.amount.toLocaleString()}</div>
                            </div>
                         </div>
                       ))}
                    </div>
                 </motion.div>
               )}

               {activeTab === 'photos' && (
                 <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
                    <div className="flex gap-3">
                       <div className="flex-1 bg-white border border-emerald-100 rounded-2xl p-3 flex flex-col items-center shadow-sm">
                          <span className="text-emerald-500 text-[11px] font-black uppercase tracking-widest italic">{fsr.todayPhotos} CAPTURED ✓</span>
                       </div>
                       <div className="flex-1 bg-white border border-rose-100 rounded-2xl p-3 flex flex-col items-center shadow-sm">
                          <span className="text-rose-500 text-[11px] font-black uppercase tracking-widest italic">{fsr.todayDone - fsr.todayPhotos} MISSING ⚠</span>
                       </div>
                       <div className="flex-1 bg-white border border-indigo-100 rounded-2xl p-3 flex flex-col items-center shadow-sm">
                          <span className="text-indigo-600 text-[11px] font-black uppercase tracking-widest italic">{(fsr.todayPhotos/Math.max(1,fsr.todayDone)*100).toFixed(0)}% RATE</span>
                       </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                       {TIMELINE_EVENTS.filter(e => e.type === 'photo').map((photo, i) => (
                         <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#F1F5F9] cursor-pointer hover:shadow-xl transition-all hover:-translate-y-1">
                            <StoreSimulation name={photo.storeName || 'Store'} time={photo.time} />
                            <div className="p-3.5 bg-white">
                               <div className="text-[13px] font-black text-[#0F172A] uppercase italic leading-none truncate">{photo.storeName}</div>
                               <div className="flex justify-between items-center mt-2 pt-2 border-t border-[#F8FAFC]">
                                  <span className="text-[11px] font-bold text-[#94A3B8] italic">{photo.time}</span>
                                  <span className="text-[10px] font-black text-emerald-500 uppercase tracking-widest italic">✓ VERIFIED</span>
                               </div>
                            </div>
                         </div>
                       ))}
                    </div>
                 </motion.div>
               )}

               {activeTab === 'performance' && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                     <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] rounded-2xl p-5 text-white shadow-xl flex flex-col justify-between overflow-hidden relative group">
                           <TrendingUp className="absolute -right-4 -bottom-4 w-24 h-24 text-white opacity-10 group-hover:scale-110 transition-transform" />
                           <div className="text-[11px] font-black text-white/70 uppercase tracking-[0.2em] italic">RANK TODAY</div>
                           <div className="text-5xl font-black italic mt-1 tracking-tighter leading-none">#{fsr.rank}</div>
                           <div className="text-[12px] font-bold text-white/80 mt-4 italic uppercase">OF 12 FIELD OFFICERS</div>
                        </div>
                        <div className="bg-white border-2 border-[#F1F5F9] rounded-2xl p-5 shadow-sm relative overflow-hidden group">
                           <div className="text-[11px] font-black text-[#4F46E5] uppercase tracking-[0.2em] italic">STREAK</div>
                           <div className="text-5xl font-black italic mt-1 tracking-tighter leading-none text-[#0F172A]">🔥 {fsr.streak}</div>
                           <div className="text-[12px] font-bold text-[#94A3B8] mt-4 uppercase italic">CONSECUTIVE DAYS</div>
                           <div className="mt-2 text-[10px] font-black text-[#64748B] bg-slate-50 px-2 rounded-full w-fit border border-slate-100">BEST: 15 DAYS</div>
                        </div>
                     </div>

                     <div className="bg-white border-2 border-[#F1F5F9] rounded-2xl p-5 shadow-sm space-y-5">
                        <span className="text-[11px] font-black text-indigo-600 uppercase tracking-[0.2em] italic block">PERFORMANCE SCORES</span>
                        {[
                          { l: 'Visit Completion', s: Math.round((fsr.todayDone/fsr.todayPlan)*100) },
                          { l: 'Photo Compliance', s: Math.round((fsr.todayPhotos/Math.max(1,fsr.todayDone))*100) },
                          { l: 'On-Time Arrival', s: 88 },
                          { l: 'Avg Time at Store', s: 72 },
                          { l: 'Booking Rate', s: Math.round((fsr.todayBookings/Math.max(1,fsr.todayDone))*100) }
                        ].map((m, i) => (
                          <div key={i} className="space-y-1.5">
                             <div className="flex justify-between items-center px-0.5">
                                <span className="text-[13px] font-bold text-[#0F172A] uppercase italic leading-none">{m.l}</span>
                                <span className={`text-[14px] font-black italic tracking-tighter leading-none ${m.s > 80 ? 'text-[#4F46E5]' : m.s > 60 ? 'text-[#D97706]' : 'text-[#EF4444]'}`}>{m.s}%</span>
                             </div>
                             <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden border border-[#E2E8F0] shadow-inner">
                                <motion.div 
                                  initial={{ width: 0 }} whileInView={{ width: `${m.s}%` }} transition={{ duration: 1 }}
                                  className={`h-full rounded-full ${m.s > 80 ? 'bg-indigo-500' : m.s > 60 ? 'bg-amber-500' : 'bg-rose-500'}`} 
                                />
                             </div>
                          </div>
                        ))}
                     </div>

                     <div className="bg-white border-2 border-[#F1F5F9] rounded-2xl p-5 shadow-sm">
                        <span className="text-[11px] font-black text-indigo-600 uppercase tracking-[0.2em] italic block mb-6">BOOKINGS — THIS WEEK</span>
                        <div className="h-[140px] w-full">
                           <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => ({ name: day, value: fsr.weekValues[i] }))}>
                                 <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fontWeight: 700, fill: '#94A3B8' }} />
                                 <Tooltip cursor={{ fill: '#F1F5F9', radius: 4 }} />
                                 <Bar dataKey="value" radius={[4, 4, 0, 0]} animationDuration={1000}>
                                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((_, index) => (
                                       <Cell key={index} fill="url(#barGrad)" />
                                    ))}
                                 </Bar>
                                 <defs>
                                    <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                                       <stop offset="0%" stopColor="#4F46E5" />
                                       <stop offset="100%" stopColor="#7C3AED" />
                                    </linearGradient>
                                 </defs>
                              </BarChart>
                           </ResponsiveContainer>
                        </div>
                     </div>

                     <div className="bg-[#FAFBFF] border-2 border-[#F1F5F9] rounded-2xl p-5 shadow-sm">
                        <span className="text-[11px] font-black text-indigo-600 uppercase tracking-[0.2em] italic block mb-4">ATTENDANCE SUMMARY</span>
                        <div className="space-y-3">
                           {[
                             { l: 'Check-In', v: fsr.checkIn || '—', c: fsr.checkIn ? 'text-emerald-500' : 'text-rose-500' },
                             { l: 'Stores Planned', v: fsr.todayPlan, c: 'text-[#0F172A]' },
                             { l: 'Stores Visited', v: fsr.todayDone, c: fsr.todayDone === fsr.todayPlan ? 'text-emerald-500' : 'text-amber-500' },
                             { l: 'KM Covered', v: `${fsr.kmCovered} KM`, c: 'text-[#0F172A]' },
                             { l: 'Missed Visits', v: Math.max(0, fsr.todayPlan - fsr.todayDone), c: fsr.todayPlan - fsr.todayDone === 0 ? 'text-emerald-600' : 'text-rose-600' }
                           ].map((item, i) => (
                             <div key={i} className="flex justify-between items-center pb-2.5 border-b border-[#F1F5F9] last:border-0 last:pb-0">
                                <span className="text-[12px] font-bold text-[#94A3B8] uppercase tracking-wide leading-none">{item.l}</span>
                                <span className={`text-[14px] font-black italic tracking-tighter leading-none ${item.c}`}>{item.v}</span>
                             </div>
                           ))}
                        </div>
                     </div>
                  </motion.div>
               )}
            </div>

            {/* PANEL FOOTER */}
            <footer className="shrink-0 h-[64px] px-5 border-t border-[#F1F5F9] flex items-center justify-between bg-white bg-white">
               <button className="h-[38px] px-5 bg-white border border-[#E2E8F0] shadow-sm rounded-xl text-[13px] font-black text-[#4F46E5] uppercase italic hover:bg-slate-50 flex items-center gap-2 transition-all">
                  <MessageCircle size={15} /> Send Reminder
               </button>
               <div className="flex gap-2">
                  <button onClick={onPrev} className="h-[38px] px-4 text-[12px] font-bold text-[#64748B] hover:bg-[#F1F5F9] rounded-xl transition-all flex items-center gap-1 uppercase italic">← Prev</button>
                  <button onClick={onNext} className="h-[38px] px-4 text-[12px] font-bold text-[#64748B] hover:bg-[#F1F5F9] rounded-xl transition-all flex items-center gap-1 uppercase italic">Next →</button>
               </div>
            </footer>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
